import { UploadOutlined } from '@ant-design/icons';
import { Button, Modal, Progress, Tabs, TabsProps, Tag, Upload, message } from 'antd';
import type { UploadFile as UploadAntFile, UploadChangeParam, UploadProps } from 'antd/es/upload';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { FileCard, type FileCard as FileCardType, GetFile, useLimitByName } from 'services';
import { useUpload } from '../../hooks/useUpload';
import { useActiveFolderId, useResetActiveFolderId } from '../../store/filepicker.store';
import styles from './FilePicker.module.scss';
import { FilePickerProps } from './FilePicker.props';
import { BrowseFilesList } from './components/BrowseFilesList';
import { SelectedFilesList } from './components/SelectedFilesList';
import { FilePickerDragger } from './components/dragger';
import { useUpdateCacheAfterUpload } from './hooks/useUpdateCacheAfterUpload';

const FilePicker: FC<FilePickerProps> = ({
  title = 'Select files',
  onFinish,
  multiple = false,
  accepted,
  close,
  initialSelectedFiles = [],
  open,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileCardType[]>(initialSelectedFiles || []);
  const fileLimit = useLimitByName('storage');
  const resetActiveFolderId = useResetActiveFolderId();
  const { updateFilesCacheAfterUploadInFilePicker } = useUpdateCacheAfterUpload({ accepted });
  const defaultActiveTab = useMemo(
    () => (initialSelectedFiles.length ? 'selected' : 'browse'),
    [initialSelectedFiles.length],
  );

  const isItemSelected = useCallback(
    (itemId: string): boolean => selectedFiles.some((item) => item.id === itemId),
    [selectedFiles],
  );

  const onToggleItem = useCallback(
    (file: FileCardType) => {
      if (multiple) {
        setSelectedFiles((prev) => {
          if (isItemSelected(file.id)) {
            return prev.filter((item) => file.id !== item.id);
          }
          return [...prev, file];
        });
      } else {
        setSelectedFiles([file]);
      }
    },
    [isItemSelected, multiple],
  );

  const handleOnFinish = useCallback(() => {
    onFinish(selectedFiles);
    close();
    resetActiveFolderId();
  }, [onFinish, close, selectedFiles, resetActiveFolderId]);

  const handleOnUploadSuccess = useCallback(
    (newFile: GetFile['file']) => {
      if (newFile) {
        const newFileCard: FileCard = {
          extension: newFile?.extension,
          id: newFile.id.toString(),
          name: newFile.name,
          size: newFile.size,
          type: newFile.type,
          url: newFile.url,
        };
        onToggleItem(newFileCard);
        updateFilesCacheAfterUploadInFilePicker(newFileCard);
      }
    },
    [onToggleItem, updateFilesCacheAfterUploadInFilePicker],
  );

  const { handleUpload, isUploadingFile, uploadProgress } = useUpload({
    onSuccess: handleOnUploadSuccess,
  });

  const folderId = useActiveFolderId();

  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    rejectedFiles.forEach(({ errors }) => {
      errors.forEach((e) => {
        if (e.code === 'too-many-files') {
          message.warning('Only single file allowed.');
        }

        if (e.code === 'file-invalid-type') {
          message.warning(e.message);
        }
      });
    });
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        message.error('File rejected');
        return;
      }
      const file = acceptedFiles[0];
      handleUpload(file, folderId);
    },
    [folderId, handleUpload],
  );

  const { getRootProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    onDropRejected,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: accepted,
  });

  const uploadButtonProps: UploadProps = useMemo(
    () => ({
      name: 'file',
      multiple: false,
      beforeUpload: () => false,
      showUploadList: false,
      accept: accepted === 'image' ? 'image/*' : undefined,
      disabled: !fileLimit?.is_allowed || isUploadingFile,
      onChange(info: UploadChangeParam<UploadAntFile<any>>) {
        handleUpload(info.file, folderId);
      },
    }),
    [fileLimit?.is_allowed, handleUpload, isUploadingFile, folderId, accepted],
  );

  const items = useMemo<TabsProps['items']>(
    () => [
      {
        key: 'browse',
        label: 'Browse',
        children: (
          <BrowseFilesList
            toggleItem={onToggleItem}
            getIsItemSelected={isItemSelected}
            accepted={accepted}
            uploadButtonProps={uploadButtonProps}
          />
        ),
      },
      {
        key: 'selected',
        label: (
          <span>
            Selected files <Tag>{selectedFiles.length}</Tag>
          </span>
        ),
        children: <SelectedFilesList items={selectedFiles} toggleItem={onToggleItem} />,
      },
    ],
    [selectedFiles, onToggleItem, isItemSelected, accepted, uploadButtonProps],
  );

  const handleModalClose = useCallback(() => {
    close();
    resetActiveFolderId();
  }, [close, resetActiveFolderId]);

  return (
    <Modal
      destroyOnClose
      title={title}
      okText="Finish"
      onOk={handleOnFinish}
      wrapClassName={styles.wrapper}
      width={648}
      height={600}
      onCancel={handleModalClose}
      open={open}
      style={{ top: 20 }}
    >
      <div className={styles.container} {...getRootProps()}>
        {isDragActive && <FilePickerDragger isMultipleUpload={false} isAbsolute isDragReject={isDragReject} />}
        {isUploadingFile && <Progress percent={uploadProgress || 0} status="active" />}
        <Tabs
          defaultActiveKey={defaultActiveTab}
          items={items}
          tabBarExtraContent={
            <Upload {...uploadButtonProps}>
              <Button icon={<UploadOutlined />} type="dashed">
                Upload more assets
              </Button>
            </Upload>
          }
        />
      </div>
    </Modal>
  );
};

const FilePickerMemo = memo(
  FilePicker,
  (prevProps, nextProps) =>
    prevProps.open === nextProps.open && prevProps.initialSelectedFiles === nextProps.initialSelectedFiles,
);

export { FilePickerMemo as FilePicker };
